/**
 * Time Tracking Hooks
 *
 * Provides hooks for clock-in/out, breaks, and lunch tracking.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { ClockActionResult, ClockSession, ClockTime, TimeEntry, TimeEntryType } from '@/types';

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface TimeEntryFilters {
  [key: string]: string | number | boolean | undefined;
  employeeId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

/**
 * Get current clock session for the logged-in user
 */
export function useCurrentSession() {
  return useQuery({
    queryKey: queryKeys.hr.timeTracking.currentSession(),
    queryFn: () => api.get<ClockSession>('/hr/time-tracking/current'),
    refetchInterval: 60_000,
  });
}

/**
 * Perform a clock action (clock_in, clock_out, break_start, break_end, lunch_start, lunch_end)
 */
export function useClockAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (type: TimeEntryType) =>
      api.post<ClockActionResult>('/hr/time-tracking/action', { type }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.hr.timeTracking.all,
      });
      // A break pauses the timed job and the break end resumes it (#3277):
      // the task cards must show the new status without a reload.
      if (result.pausedWorkOrderIds?.length || result.resumedWorkOrderIds?.length) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
      }
    },
  });
}

/**
 * Get first clock-in and last clock-out per employee per day (for shift calendar)
 */
export function useClockTimes(from: string, to: string) {
  return useQuery({
    queryKey: queryKeys.hr.timeTracking.clockTimes(from, to),
    queryFn: () =>
      api.get<{ data: ClockTime[] }>('/hr/time-tracking/clock-times', {
        params: { from, to },
      }),
    select: (res) => res.data,
    enabled: !!from && !!to,
    placeholderData: keepPreviousData,
  });
}

/**
 * Get time entries list (for HR tab)
 */
export function useTimeEntries(filters: TimeEntryFilters) {
  return useInfiniteQuery({
    queryKey: queryKeys.hr.timeTracking.entries(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<TimeEntry>>('/hr/time-tracking/entries', {
        params: {
          ...filters,
          cursor: pageParam as string | undefined,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.hasMore ? lastPage.pagination.nextCursor : undefined,
  });
}
